import type { DutyAnalyticsAttendanceFiguresDto } from '@pams/shared';
import { MARKING_COLUMNS, drillHref, type DrillCell, type DutyAnalyticsWindowParam } from '@/lib/dutyAnalytics';
import { cn } from '@/lib/utils';
import { DUTY_ANALYTICS_COPY } from '../copy';
import { CountLink } from './CountLink';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2 · U1] WHO ENTERED THE MARKINGS — TWO COUNTS PER ROW, AND NO TOTAL COLUMN.
 *
 *  ⚠️ "MARKINGS ONLY" IS THE USER'S DECISION (U1), AND THE BAND IS NOTHING ELSE. One count is the taps
 *  an officer made on their own duty, the other the taps somebody else on the duty entered for them —
 *  [C1c §2d]'s SELF and ON_BEHALF. A marking the app derives beside a tap is counted nowhere, and the
 *  officers counted are the force on the duty: who supervised it is not a figure on this page.
 *
 *  ⚠️ NEVER A SUM. The two answer "who said so", and a column adding them would erase the one thing
 *  the band exists to show; `MarkingsBand.test.tsx` holds that no cell carries their total.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

export type MarkingsRow = {
  readonly key: string;
  readonly orgUnitId: string | null;
  readonly label: string;
  readonly figures: DutyAnalyticsAttendanceFiguresDto;
  readonly cell: DrillCell;
  readonly testId?: string;
};

const CELL = 'h-10 whitespace-nowrap border-b border-border px-3';
const HEAD = 'h-10 whitespace-nowrap border-b border-border px-3 text-xs font-medium text-muted-foreground';

export const MarkingsBand = ({
  rows,
  footer,
  nameHeader,
  range,
  pageDivision,
  asDivisionId,
}: {
  rows: readonly MarkingsRow[];
  footer: MarkingsRow;
  nameHeader: string;
  range: DutyAnalyticsWindowParam;
  /** The page's own `?division=`, carried into every drill link for the way back (`drillHref`). */
  pageDivision?: string;
  /** [P7C.1 §4] The division this page is read AS, from the commissionerate — sent with every drill. */
  asDivisionId?: string;
}) => {
  const line = (row: MarkingsRow, total: boolean) => (
    <tr
      key={row.key}
      data-testid={row.testId ?? 'duty-analytics-markings-row'}
      {...(row.orgUnitId === null ? {} : { 'data-org-unit-id': row.orgUnitId })}
    >
      <th scope="row" className={cn(CELL, 'text-left', total ? 'font-medium' : 'font-normal')}>
        {row.label}
      </th>
      {MARKING_COLUMNS.map((column) => (
        <td key={column.key} className={cn(CELL, 'text-right tabular-nums')}>
          <CountLink
            value={row.figures[column.key]}
            metric={column.metric}
            href={drillHref(column.metric, row.cell, range, pageDivision, asDivisionId)}
            label={DUTY_ANALYTICS_COPY.countLabel(column.metric, row.figures[column.key], row.label)}
          />
        </td>
      ))}
    </tr>
  );

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">{DUTY_ANALYTICS_COPY.markingsSentence}</p>
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0 text-sm" data-testid="duty-analytics-markings-table">
          <thead>
            <tr>
              <th scope="col" className={cn(HEAD, 'text-left')}>
                {nameHeader}
              </th>
              {MARKING_COLUMNS.map((column) => (
                <th key={column.key} scope="col" className={cn(HEAD, 'text-right')}>
                  {DUTY_ANALYTICS_COPY.columns[column.metric]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{rows.map((row) => line(row, false))}</tbody>
          <tfoot>{line(footer, true)}</tfoot>
        </table>
      </div>
    </div>
  );
};
